import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Collapse,
  Stack,
  FormControlLabel,
  Checkbox,
  Drawer,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Refresh,
  Delete,
  Search,
  ExpandMore,
  ExpandLess,
  UnfoldMore,
  UnfoldLess,
  ArrowBack,
  Close,
  Circle,
  PlayArrow,
  Stop,
  Timer,
} from "@mui/icons-material";
import {
  fetchMonitoringStatus,
  toggleMonitoring,
  fetchTraces,
  fetchTraceDetail,
  clearAllTraces,
  clearSelectedTrace,
} from "../redux/slices/monitoringSlice";

const POLL_INTERVAL = 10000;

// Format ms into "M:SS" countdown string
function formatCountdown(ms) {
  const totalSec = Math.ceil(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, "0")} remaining`;
}

// Color scheme for span types
const SPAN_COLORS = {
  db: "#4caf50",
  wp: "#2196f3",
};

export default function MonitoringTab({ siteId }) {
  const dispatch = useDispatch();
  const {
    enabled,
    expiresAt,
    remainingMs: initialRemainingMs,
    durationMin: activeDuration,
    traces,
    selectedTrace,
    statusLoading,
    tracesLoading,
    traceLoading,
    toggleLoading,
    error,
  } = useSelector((state) => state.monitoring);

  const [search, setSearch] = useState("");
  const [durationMin, setDurationMin] = useState(5);
  const [countdown, setCountdown] = useState(0);
  const [expandedSpans, setExpandedSpans] = useState(new Set());
  const [condensed, setCondensed] = useState(false);
  const [slowOnly, setSlowOnly] = useState(false);
  const [spanDetailOpen, setSpanDetailOpen] = useState(false);
  const [spanDetail, setSpanDetail] = useState(null);
  const pollRef = useRef(null);
  const countdownRef = useRef(null);

  // Fetch status on mount
  useEffect(() => {
    dispatch(fetchMonitoringStatus(siteId));
  }, [dispatch, siteId]);

  // Countdown timer — ticks every second when enabled
  useEffect(() => {
    if (enabled && expiresAt) {
      const tick = () => {
        const ms = Math.max(0, new Date(expiresAt).getTime() - Date.now());
        setCountdown(ms);
        if (ms <= 0) {
          clearInterval(countdownRef.current);
          dispatch(fetchMonitoringStatus(siteId));
        }
      };
      tick();
      countdownRef.current = setInterval(tick, 1000);
      return () => clearInterval(countdownRef.current);
    } else {
      setCountdown(0);
    }
    return () => clearInterval(countdownRef.current);
  }, [enabled, expiresAt, dispatch, siteId]);

  // Auto-poll traces when enabled
  useEffect(() => {
    if (enabled && !selectedTrace) {
      const poll = () => dispatch(fetchTraces({ siteId, search }));
      poll();
      pollRef.current = setInterval(poll, POLL_INTERVAL);
      return () => clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [dispatch, siteId, enabled, search, selectedTrace]);

  const handleStart = useCallback(() => {
    dispatch(toggleMonitoring({ siteId, enable: true, durationMin }));
  }, [dispatch, siteId, durationMin]);

  const handleStop = useCallback(() => {
    dispatch(toggleMonitoring({ siteId, enable: false }));
  }, [dispatch, siteId]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchTraces({ siteId, search }));
  }, [dispatch, siteId, search]);

  const handleClear = useCallback(() => {
    if (window.confirm("Delete all traces for this site?")) {
      dispatch(clearAllTraces(siteId));
    }
  }, [dispatch, siteId]);

  const handleTraceClick = useCallback(
    (traceId) => {
      dispatch(fetchTraceDetail({ siteId, traceId }));
    },
    [dispatch, siteId],
  );

  const handleBack = useCallback(() => {
    dispatch(clearSelectedTrace());
  }, [dispatch]);

  const toggleSpan = useCallback((spanId) => {
    setExpandedSpans((prev) => {
      const next = new Set(prev);
      if (next.has(spanId)) {
        next.delete(spanId);
      } else {
        next.add(spanId);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    if (!selectedTrace?.spans) return;
    const all = new Set();
    const collect = (spans) => {
      spans.forEach((s) => {
        if (s.children?.length) {
          all.add(s.id);
          collect(s.children);
        }
      });
    };
    collect(selectedTrace.spans);
    setExpandedSpans(all);
  }, [selectedTrace]);

  const collapseAll = useCallback(() => {
    setExpandedSpans(new Set());
  }, []);

  // === Trace Detail View ===
  if (selectedTrace) {
    return (
      <TraceDetailView
        trace={selectedTrace}
        loading={traceLoading}
        onBack={handleBack}
        expandedSpans={expandedSpans}
        toggleSpan={toggleSpan}
        expandAll={expandAll}
        collapseAll={collapseAll}
        condensed={condensed}
        setCondensed={setCondensed}
        slowOnly={slowOnly}
        setSlowOnly={setSlowOnly}
        spanDetailOpen={spanDetailOpen}
        setSpanDetailOpen={setSpanDetailOpen}
        spanDetail={spanDetail}
        setSpanDetail={setSpanDetail}
      />
    );
  }

  // === Traces List View ===
  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header — On-demand controls with timer */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h6">Request Traces</Typography>
          {enabled && countdown > 0 && (
            <Chip
              icon={<Timer />}
              label={formatCountdown(countdown)}
              color="primary"
              size="small"
              variant="outlined"
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {!enabled && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Duration</InputLabel>
              <Select
                value={durationMin}
                onChange={(e) => setDurationMin(e.target.value)}
                label="Duration"
              >
                {[5, 10, 15, 20, 30].map((m) => (
                  <MenuItem key={m} value={m}>
                    {m} minutes
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!enabled ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={
                toggleLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PlayArrow />
                )
              }
              onClick={handleStart}
              disabled={toggleLoading || statusLoading}
              size="small"
            >
              Start Tracing
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              startIcon={
                toggleLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Stop />
                )
              }
              onClick={handleStop}
              disabled={toggleLoading}
              size="small"
            >
              Stop
            </Button>
          )}
          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={!enabled || tracesLoading}
                size="small"
              >
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Clear all traces">
            <span>
              <IconButton
                onClick={handleClear}
                disabled={!traces.length}
                size="small"
                color="error"
              >
                <Delete />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Search */}
      {enabled && (
        <TextField
          size="small"
          placeholder="Search by URL..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      )}

      {!enabled && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Enable monitoring to start capturing request traces. The APM plugin
          will be deployed to your WordPress site automatically.
        </Alert>
      )}

      {enabled && tracesLoading && traces.length === 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {enabled && !tracesLoading && traces.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No traces captured yet. Browse your site to generate requests.
        </Alert>
      )}

      {/* Traces Table */}
      {traces.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>URL</TableCell>
                <TableCell align="center">Method</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell align="center">Spans</TableCell>
                <TableCell align="center">DB Queries</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {traces.map((trace) => (
                <TableRow
                  key={trace.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleTraceClick(trace.id)}
                >
                  <TableCell
                    sx={{
                      maxWidth: 300,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Typography variant="body2" color="primary">
                      {trace.url}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={trace.method}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color={
                        trace.duration_ms > 1000
                          ? "error"
                          : trace.duration_ms > 500
                            ? "warning.main"
                            : "text.primary"
                      }
                      fontWeight="medium"
                    >
                      {trace.duration_ms?.toFixed(1)}ms
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{trace.total_spans}</TableCell>
                  <TableCell align="center">{trace.db_spans}</TableCell>
                  <TableCell align="center">
                    {trace.has_errors ? (
                      <Chip label="ERROR" size="small" color="error" />
                    ) : (
                      <Chip
                        label={trace.status_code || 200}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" color="text.secondary">
                      {new Date(trace.timestamp).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ============================================================================
// Trace Detail View
// ============================================================================

function TraceDetailView({
  trace,
  loading,
  onBack,
  expandedSpans,
  toggleSpan,
  expandAll,
  collapseAll,
  condensed,
  setCondensed,
  slowOnly,
  setSlowOnly,
  spanDetailOpen,
  setSpanDetailOpen,
  spanDetail,
  setSpanDetail,
}) {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!trace) return null;

  const dbPct =
    trace.durationMs > 0
      ? ((trace.dbTimeMs / trace.durationMs) * 100).toFixed(1)
      : 0;
  const wpPct = (100 - dbPct).toFixed(1);

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={onBack} size="small">
          <ArrowBack />
        </IconButton>
        <Typography variant="h6">Trace Detail</Typography>
      </Stack>

      {/* Summary Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap" }}>
        <SummaryCard label="URL" value={trace.url} />
        <SummaryCard
          label="Duration"
          value={`${trace.durationMs?.toFixed(1)}ms`}
        />
        <SummaryCard label="Method" value={trace.method} />
        <SummaryCard label="Total Spans" value={trace.totalSpans} />
        <SummaryCard label="DB Queries" value={trace.dbSpans} />
        <SummaryCard
          label="Status"
          value={
            <Chip
              label={trace.hasErrors ? "ERROR" : "SUCCESS"}
              size="small"
              color={trace.hasErrors ? "error" : "success"}
            />
          }
        />
      </Stack>

      {/* Latency Breakdown */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Latency Breakdown
        </Typography>
        <Stack direction="row" spacing={3} alignItems="center">
          <LatencyBar dbPct={parseFloat(dbPct)} wpPct={parseFloat(wpPct)} />
          <Stack direction="row" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Circle sx={{ fontSize: 10, color: SPAN_COLORS.db }} />
              <Typography variant="caption">
                DB {dbPct}% ({trace.dbTimeMs?.toFixed(1)}ms)
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Circle sx={{ fontSize: 10, color: SPAN_COLORS.wp }} />
              <Typography variant="caption">
                WP {wpPct}% ({trace.wpTimeMs?.toFixed(1)}ms)
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {/* Span Controls */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        <Button size="small" startIcon={<UnfoldMore />} onClick={expandAll}>
          Expand All
        </Button>
        <Button size="small" startIcon={<UnfoldLess />} onClick={collapseAll}>
          Collapse All
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={condensed}
              onChange={(e) => setCondensed(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Condensed</Typography>}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={slowOnly}
              onChange={(e) => setSlowOnly(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Slow spans (&gt;10ms)</Typography>}
        />
      </Stack>

      {/* Span Tree */}
      <Paper variant="outlined">
        <SpanTree
          spans={trace.spans || []}
          totalDuration={trace.durationMs}
          expandedSpans={expandedSpans}
          toggleSpan={toggleSpan}
          condensed={condensed}
          slowOnly={slowOnly}
          depth={0}
          onSpanClick={(span) => {
            setSpanDetail(span);
            setSpanDetailOpen(true);
          }}
        />
      </Paper>

      {/* Span Detail Drawer */}
      <Drawer
        anchor="right"
        open={spanDetailOpen}
        onClose={() => setSpanDetailOpen(false)}
      >
        <Box sx={{ width: 380, p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Span Detail</Typography>
            <IconButton onClick={() => setSpanDetailOpen(false)} size="small">
              <Close />
            </IconButton>
          </Stack>
          {spanDetail && <SpanDetailContent span={spanDetail} />}
        </Box>
      </Drawer>
    </Box>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function SummaryCard({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1, minWidth: 100 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight="medium"
        sx={{
          maxWidth: 200,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
}

function LatencyBar({ dbPct, wpPct }) {
  return (
    <Box
      sx={{
        display: "flex",
        height: 20,
        borderRadius: 1,
        overflow: "hidden",
        width: 200,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          width: `${dbPct}%`,
          bgcolor: SPAN_COLORS.db,
          minWidth: dbPct > 0 ? 2 : 0,
        }}
      />
      <Box
        sx={{
          width: `${wpPct}%`,
          bgcolor: SPAN_COLORS.wp,
          minWidth: wpPct > 0 ? 2 : 0,
        }}
      />
    </Box>
  );
}

function SpanTree({
  spans,
  totalDuration,
  expandedSpans,
  toggleSpan,
  condensed,
  slowOnly,
  depth,
  onSpanClick,
}) {
  return spans
    .filter((span) => !slowOnly || span.duration > 10)
    .map((span) => {
      const hasChildren = span.children && span.children.length > 0;
      const isExpanded = expandedSpans.has(span.id);
      const pct =
        totalDuration > 0
          ? ((span.duration / totalDuration) * 100).toFixed(1)
          : 0;
      const color = span.type === "db" ? SPAN_COLORS.db : SPAN_COLORS.wp;

      return (
        <Box key={span.id}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pl: depth * 3 + 1,
              pr: 1,
              py: condensed ? 0.25 : 0.75,
              borderBottom: "1px solid",
              borderColor: "divider",
              "&:hover": { bgcolor: "action.hover" },
              cursor: "pointer",
            }}
            onClick={() => onSpanClick(span)}
          >
            {/* Expand/Collapse */}
            <Box sx={{ width: 24, flexShrink: 0 }}>
              {hasChildren && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSpan(span.id);
                  }}
                  sx={{ p: 0 }}
                >
                  {isExpanded ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              )}
            </Box>

            {/* Children count badge */}
            {hasChildren && (
              <Chip
                label={span.children.length}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  mr: 1,
                  bgcolor: color,
                  color: "#fff",
                }}
              />
            )}

            {/* Color indicator */}
            <Circle sx={{ fontSize: 8, color, mr: 1, flexShrink: 0 }} />

            {/* Span name */}
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                fontFamily: span.type === "db" ? "monospace" : "inherit",
                fontSize: span.type === "db" ? "0.75rem" : "0.8rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {span.name}
            </Typography>

            {/* Duration */}
            <Typography
              variant="caption"
              sx={{ ml: 2, minWidth: 70, textAlign: "right", flexShrink: 0 }}
              color={
                span.duration > 100
                  ? "error"
                  : span.duration > 50
                    ? "warning.main"
                    : "text.secondary"
              }
              fontWeight="medium"
            >
              {span.duration?.toFixed(2)}ms
            </Typography>

            {/* Percentage */}
            <Typography
              variant="caption"
              sx={{ ml: 1, minWidth: 45, textAlign: "right", flexShrink: 0 }}
              color="text.secondary"
            >
              {pct}%
            </Typography>
          </Box>

          {/* Children */}
          {hasChildren && (
            <Collapse in={isExpanded}>
              <SpanTree
                spans={span.children}
                totalDuration={totalDuration}
                expandedSpans={expandedSpans}
                toggleSpan={toggleSpan}
                condensed={condensed}
                slowOnly={slowOnly}
                depth={depth + 1}
                onSpanClick={onSpanClick}
              />
            </Collapse>
          )}
        </Box>
      );
    });
}

function SpanDetailContent({ span }) {
  const fields = [
    { label: "Name", value: span.name },
    { label: "Type", value: span.type },
    { label: "ID", value: span.id },
    { label: "Parent ID", value: span.parentId || "—" },
    { label: "Duration", value: `${span.duration?.toFixed(2)}ms` },
    {
      label: "Percentage",
      value: span.pct !== undefined ? `${span.pct.toFixed(1)}%` : "—",
    },
  ];

  if (span.sql) {
    fields.push({ label: "SQL", value: span.sql, mono: true });
  }
  if (span.plugin) {
    fields.push({ label: "Plugin", value: span.plugin });
  }

  return (
    <Stack spacing={2}>
      {fields.map((f) => (
        <Box key={f.label}>
          <Typography variant="caption" color="text.secondary">
            {f.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              wordBreak: "break-all",
              fontFamily: f.mono ? "monospace" : "inherit",
              fontSize: f.mono ? "0.75rem" : "inherit",
            }}
          >
            {f.value}
          </Typography>
        </Box>
      ))}
      {span.children?.length > 0 && (
        <Box>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Children: {span.children.length} spans
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
